import { z } from 'zod';
import { ConnectorMode } from './enums.js';
import { IsoDateTime, UserRef, Uuid } from './common.js';

export const IntegrationProvider = z.enum(['GITHUB', 'C4C7US', 'JIRA', 'SLACK', 'EMAIL', 'AI', 'STORAGE', 'SCANNER', 'VAMMO_AUTH']);
export type IntegrationProvider = z.infer<typeof IntegrationProvider>;

export const ConnectorHealth = z.enum(['HEALTHY', 'DEGRADED', 'FAILING', 'DISABLED', 'UNVERIFIED']);
export type ConnectorHealth = z.infer<typeof ConnectorHealth>;

export const IntegrationConnectionView = z.object({
  id: Uuid,
  provider: IntegrationProvider,
  displayName: z.string(),
  mode: ConnectorMode,
  health: ConnectorHealth,
  scope: z.record(z.string(), z.unknown()),
  lastSuccessAt: IsoDateTime.nullable(),
  lastAttemptAt: IsoDateTime.nullable(),
  lastSafeError: z.string().nullable(),
  circuitOpenUntil: IsoDateTime.nullable(),
  consecutiveFailures: z.number().int(),
  liveVerifiedAt: IsoDateTime.nullable(),
  warnings: z.array(z.string()),
  cursors: z.array(z.object({ name: z.string(), position: z.string().nullable(), updatedAt: IsoDateTime })),
});
export type IntegrationConnectionView = z.infer<typeof IntegrationConnectionView>;

export const ExceptionKind = z.enum([
  'UNMATCHED_WORK',
  'UNCERTAIN_ASSOCIATION',
  'UNMAPPED_IDENTITY',
  'UNMAPPED_REPOSITORY',
  'UNMAPPED_SERVICE',
  'CONNECTOR_FAILURE',
  'DUPLICATE_CORRECTION',
  'WEBHOOK_INTEGRITY',
  'DEPLOYMENT_ORDERING',
  'DELIVERY_UNKNOWN',
  'IMPORT_EXCEPTION',
  'ACL_QUARANTINE',
  'SCOPE_REVIEW',
  'COMPLETION_BLOCKED',
]);
export type ExceptionKind = z.infer<typeof ExceptionKind>;

export const ExceptionView = z.object({
  id: Uuid,
  kind: ExceptionKind,
  provider: z.string().nullable(),
  status: z.enum(['OPEN', 'RESOLVED', 'IGNORED']),
  summary: z.string(),
  subjectKind: z.string().nullable(),
  subjectId: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  occurrences: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  resolvedBy: UserRef.nullable(),
  resolution: z.string().nullable(),
});
export type ExceptionView = z.infer<typeof ExceptionView>;

export const ResolveException = z
  .object({ status: z.enum(['RESOLVED', 'IGNORED']), resolution: z.string().min(1).max(1000) })
  .strict();

export const DeadLetterView = z.object({
  id: z.string(),
  queue: z.string(),
  createdAt: IsoDateTime,
  failedAt: IsoDateTime.nullable(),
  attempts: z.number().int(),
  safeError: z.string().nullable(),
  idempotencyKey: z.string().nullable(),
});
export type DeadLetterView = z.infer<typeof DeadLetterView>;

export const OperationsStatus = z.object({
  queues: z.array(z.object({ name: z.string(), queued: z.number().int(), active: z.number().int(), oldestQueuedSeconds: z.number().nullable(), deadLetters: z.number().int() })),
  outbox: z.object({ pending: z.number().int(), oldestPendingSeconds: z.number().nullable() }),
  worker: z.object({ lastHeartbeatAt: IsoDateTime.nullable(), healthy: z.boolean() }),
  alerts: z.array(z.object({ code: z.string(), message: z.string() })),
  aiUsageToday: z.object({ tokens: z.number().int(), limit: z.number().int(), blocked: z.boolean() }),
});
export type OperationsStatus = z.infer<typeof OperationsStatus>;

// ---------------------------------------------------------------- Jira import

export const ImportMode = z.enum(['DISCOVERY', 'DRY_RUN', 'SAMPLE', 'FULL', 'DELTA', 'RECONCILE']);
export type ImportMode = z.infer<typeof ImportMode>;

export const ImportRunView = z.object({
  id: Uuid,
  mode: ImportMode,
  status: z.enum(['QUEUED', 'RUNNING', 'PAUSED', 'SUCCEEDED', 'FAILED', 'CANCELED']),
  sourceInstance: z.string(),
  connectorMode: ConnectorMode,
  projectKeys: z.array(z.string()),
  startedBy: UserRef.nullable(),
  startedAt: IsoDateTime.nullable(),
  finishedAt: IsoDateTime.nullable(),
  upperWatermark: IsoDateTime.nullable(),
  counts: z.record(z.string(), z.number()),
  exceptionCount: z.number().int(),
  checkpoint: z.string().nullable(),
  reconciliation: z.record(z.string(), z.unknown()).nullable(),
  createdAt: IsoDateTime,
});
export type ImportRunView = z.infer<typeof ImportRunView>;

export const StartImport = z
  .object({
    mode: ImportMode,
    projectKeys: z.array(z.string().regex(/^[A-Z][A-Z0-9_]{0,20}$/)).max(200).optional(),
    sampleSize: z.number().int().min(1).max(500).optional(),
  })
  .strict();
export type StartImport = z.infer<typeof StartImport>;

export const ImportMappingView = z.object({
  sourceInstance: z.string(),
  projects: z.array(
    z.object({
      sourceKey: z.string(),
      sourceName: z.string(),
      targetProjectId: Uuid.nullable(),
      targetTeamId: Uuid.nullable(),
      requestIssueTypes: z.array(z.string()),
      statusMap: z.record(z.string(), z.string()),
      approved: z.boolean(),
    }),
  ),
  securityLevels: z.array(z.object({ sourceId: z.string(), name: z.string(), groupId: Uuid.nullable() })),
  version: z.number().int(),
});
export type ImportMappingView = z.infer<typeof ImportMappingView>;

export const SetImportMapping = z
  .object({
    expectedVersion: z.number().int(),
    projects: z
      .array(
        z
          .object({
            sourceKey: z.string(),
            targetProjectId: Uuid.nullable(),
            targetTeamId: Uuid.nullable(),
            requestIssueTypes: z.array(z.string().max(100)).max(50),
            statusMap: z.record(z.string().max(100), z.enum(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE', 'CANCELED'])),
            approved: z.boolean(),
          })
          .strict(),
      )
      .max(200),
    securityLevels: z.array(z.object({ sourceId: z.string(), groupId: Uuid.nullable() }).strict()).max(200),
  })
  .strict();
export type SetImportMapping = z.infer<typeof SetImportMapping>;

// ---------------------------------------------------------------- Identity linking (Section 4.2)

/** An external account an administrator may link: an unmapped PR author or a Jira historical actor. */
export const IdentityCandidate = z.object({
  provider: z.enum(['GITHUB', 'JIRA']),
  externalAccountId: z.string(),
  displayHandle: z.string().nullable(),
  /** The no-login historical user currently holding a Jira account, if any. */
  historicalUserId: z.string().nullable(),
  occurrences: z.number().int(),
});
export type IdentityCandidate = z.infer<typeof IdentityCandidate>;

export const LinkIdentity = z
  .object({
    provider: z.enum(['GITHUB', 'JIRA']),
    externalAccountId: z.string().min(1).max(200),
    displayHandle: z.string().max(200).optional(),
    reason: z.string().min(1).max(500),
  })
  .strict();
export type LinkIdentity = z.infer<typeof LinkIdentity>;

/** Owner-approved Jira account to corporate email mapping (ADR-0015). */
export const PreprovisionJiraPeople = z
  .object({
    reason: z.string().min(1).max(500),
    people: z
      .array(
        z
          .object({
            email: z.email().max(320),
            displayName: z.string().min(1).max(200),
            jiraAccountIds: z.array(z.string().min(1).max(200)).min(1).max(10),
          })
          .strict(),
      )
      .min(1)
      .max(500),
  })
  .strict();
export type PreprovisionJiraPeople = z.infer<typeof PreprovisionJiraPeople>;

export const PreprovisionedPerson = z.object({
  email: z.string(),
  userId: z.string().nullable(),
  outcome: z.enum(['CREATED', 'REUSED', 'SKIPPED']),
  linkedAccounts: z.number().int(),
  movedContributions: z.number().int(),
  skipReason: z.string().nullable(),
});
export const PreprovisionJiraPeopleResult = z.object({
  sourceInstance: z.string(),
  created: z.number().int(),
  reused: z.number().int(),
  skipped: z.number().int(),
  linkedAccounts: z.number().int(),
  people: z.array(PreprovisionedPerson),
});
export type PreprovisionJiraPeopleResult = z.infer<typeof PreprovisionJiraPeopleResult>;

export const LinkedIdentityView = z.object({
  id: z.string(),
  provider: z.string(),
  externalAccountId: z.string(),
  displayHandle: z.string().nullable(),
  verificationMethod: z.string(),
  verifiedAt: z.string().nullable(),
});
export type LinkedIdentityView = z.infer<typeof LinkedIdentityView>;
